import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import "./../App.css";

const Table = ({
  rows,
  columns,
  currentPage,
  pageSize,
  totalRecords,
  onPageChange,
  pageSizeOptions = [10, 25, 50, 100],
  onSort,
  paginationMode = "server",
}) => {
  // State management
  const [paginatedRows, setPaginatedRows] = useState([]);
  const [pageInput, setPageInput] = useState(currentPage);
  const [paginationModel, setPaginationModel] = useState({
    page: currentPage - 1, // DataGrid uses 0-based indexing
    pageSize: pageSize,
  });

  // Update rows when data changes
  useEffect(() => {
    setPaginatedRows(rows);
  }, [rows]);

  // Update pagination model when page or size changes
  useEffect(() => {
    setPaginationModel({
      page: currentPage - 1, // Convert to 0-based for DataGrid
      pageSize: pageSize,
    });
    setPageInput(currentPage);
  }, [currentPage, pageSize]);

  // Pagination handlers
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
    if (onPageChange) {
      onPageChange(newModel.page + 1, newModel.pageSize); // Convert back to 1-based for API
    }
  };

  const handleSortChange = (sortModel) => {
    if (onSort && sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      onSort(field, sort);
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    const numValue = parseInt(value);
    const totalPages = Math.ceil(totalRecords / pageSize);

    if (!isNaN(numValue) && numValue >= 0) {
      setPageInput(value);
    }
  };

  const handlePageJump = () => {
    const numPage = parseInt(pageInput);
    const totalPages = Math.ceil(totalRecords / pageSize);

    if (numPage > 0 && numPage <= totalPages) {
      onPageChange(numPage, pageSize);
    } else {
      // Reset to current page if invalid input
      setPageInput(currentPage);
    }
  };

  // Handle Enter key in page input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePageJump();
    }
  };

  const customStyles = {
    paginationContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "20px 0",
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      margin: "16px 0",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    },
    jumpToPageBox: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "8px 16px",
      backgroundColor: "white",
      borderRadius: "6px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    pageLabel: {
      fontWeight: "500",
      color: "#555",
    },
    pageInput: {
      width: "60px",
      padding: "8px 12px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      fontSize: "14px",
      outline: "none",
      transition: "border-color 0.2s",
      "&:focus": {
        borderColor: "#2196f3",
        boxShadow: "0 0 0 2px rgba(33,150,243,0.1)",
      },
    },
    goButton: {
      padding: "8px 16px",
      backgroundColor: "#3f506b",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      transition: "background-color 0.2s",
      "&:hover": {
        backgroundColor: "#1976d2",
      },
    },
  };

  return (
    <div>
      <DataGrid
        rows={paginatedRows}
        columns={columns}
        paginationMode={paginationMode}
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={pageSizeOptions}
        pagination
        onSortModelChange={handleSortChange}
        autoHeight // This will make the DataGrid adjust its height based on the number of rows
        sx={{
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f5f5f5",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #e0e0e0",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#fafafa",
            borderBottom: "2px solid #e0e0e0",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "2px solid #e0e0e0",
          },
        }}
        disableRowSelectionOnClick
        disableColumnFilter
      />

      <div style={customStyles.paginationContainer}>
        <div style={customStyles.jumpToPageBox}>
          <span style={customStyles.pageLabel}>Jump to page:</span>
          <input
            type="number"
            value={pageInput}
            onChange={handlePageInputChange}
            onKeyPress={handleKeyPress}
            min={1}
            max={Math.ceil(totalRecords / pageSize)}
            style={customStyles.pageInput}
          />
          <button 
            onClick={handlePageJump}
            style={customStyles.goButton}
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
};

export default Table;